// Theme presets for Tailwind color tokens using CSS variables
// Each preset is a record of variable names and HSL values

export type ThemePreset = {
  name: string;
  type: string;
  variables: Record<string, string>;
  dark?: Record<string, string>;
};

export const themePresets: ThemePreset[] = [
  {
    name: 'Default',
    type: 'default',
    // First-paint fallback used before `/api/themes` returns or when
    // the runtime preset cache (localStorage `db-theme-presets`) is
    // empty. Keep these in sync with the DB-side "Default" theme so
    // the visual doesn't flip after hydration completes.
    variables: {
      '--background': '240 6% 6%',
      '--foreground': '0 0% 98%',
      '--card': '240 6% 8%',
      '--card-foreground': '0 0% 98%',
      '--popover': '240 6% 8%',
      '--popover-foreground': '0 0% 98%',
      '--primary': '270 70% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '240 4% 16%',
      '--secondary-foreground': '0 0% 98%',
      '--muted': '240 4% 16%',
      '--muted-foreground': '240 5% 65%',
      '--accent': '240 4% 16%',
      '--accent-foreground': '0 0% 98%',
      '--destructive': '0 62.8% 30.6%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '240 4% 18%',
      '--input': '240 4% 18%',
      '--ring': '270 70% 60%',
    },
  },

  {
    name: 'AMOLED',

    type: 'free',

    variables: {
      '--background': '0 0% 0%',

      '--foreground': '0 0% 100%',

      '--card': '0 0% 0%',

      '--card-foreground': '0 0% 100%',

      '--popover': '0 0% 0%',

      '--popover-foreground': '0 0% 100%',
      '--primary': '0 0% 100%',
      '--primary-foreground': '0 0% 0%',
      '--secondary': '0 0% 20%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 40%',
      '--muted-foreground': '0 0% 100%',
      '--accent': '0 0% 100%',
      '--accent-foreground': '0 0% 0%',
      '--destructive': '0 84.2% 60.2%',

      '--destructive-foreground': '0 0% 100%',

      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '220 90% 56%',
    },
  },

  {
    name: 'Pastel',
    type: 'free-temp',
    variables: {
      '--background': '210 60% 98%',
      '--foreground': '230 15% 30%',
      '--card': '210 60% 95%',
      '--card-foreground': '230 15% 30%',
      '--popover': '210 60% 95%',
      '--popover-foreground': '230 15% 30%',
      '--primary': '270 60% 80%',
      '--primary-foreground': '230 15% 30%',
      '--secondary': '160 60% 80%',
      '--secondary-foreground': '230 15% 30%',
      '--muted': '40 60% 85%',
      '--muted-foreground': '230 15% 45%',
      '--accent': '340 60% 80%',
      '--accent-foreground': '230 15% 30%',
      '--destructive': '0 80% 80%',
      '--destructive-foreground': '230 15% 30%',
      '--border': '210 60% 90%',
      '--input': '210 60% 90%',
      '--ring': '270 60% 80%',
    },
  },
  {
    name: 'AMOLED Blue',

    type: 'free',

    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',

      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '220 90% 56%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '200 100% 50%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '220 90% 56%',
    },
  },
  {
    name: 'AMOLED Purple',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '265 90% 70%',
      '--primary-foreground': '0 0% 12%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '290 100% 70%',
      '--accent-foreground': '0 0% 12%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '265 90% 70%',
    },
  },
  {
    name: 'AMOLED Red',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '0 84% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '12 90% 60%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '0 84% 60%',
    },
  },
  {
    name: 'AMOLED Orange',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '24 95% 58%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '32 100% 60%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '24 95% 58%',
    },
  },
  {
    name: 'AMOLED Yellow',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '48 100% 50%',
      '--primary-foreground': '0 0% 10%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '45 100% 60%',
      '--accent-foreground': '0 0% 10%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '48 100% 50%',
    },
  },
  {
    name: 'AMOLED Green',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '135 70% 45%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '160 100% 40%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '135 70% 45%',
    },
  },
  {
    name: 'AMOLED Teal',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '168 76% 42%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '180 100% 45%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '168 76% 42%',
    },
  },
  {
    name: 'AMOLED Cyan',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '190 100% 50%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '200 100% 60%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '190 100% 50%',
    },
  },
  {
    name: 'AMOLED Pink',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '330 90% 65%',
      '--primary-foreground': '0 0% 10%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '320 100% 70%',
      '--accent-foreground': '0 0% 10%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '330 90% 65%',
    },
  },
  {
    name: 'AMOLED Lime',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '90 100% 50%',
      '--primary-foreground': '0 0% 10%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '100 100% 50%',
      '--accent-foreground': '0 0% 10%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '90 100% 50%',
    },
  },
  {
    name: 'AMOLED Indigo',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '235 100% 62%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '250 100% 70%',
      '--accent-foreground': '0 0% 12%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '235 100% 62%',
    },
  },
  {
    name: 'AMOLED Gold',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '45 90% 55%',
      '--primary-foreground': '0 0% 10%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '50 100% 60%',
      '--accent-foreground': '0 0% 10%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '45 90% 55%',
    },
  },
  {
    name: 'AMOLED Rose',
    type: 'free',
    variables: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--card': '0 0% 0%',
      '--card-foreground': '0 0% 100%',
      '--popover': '0 0% 0%',
      '--popover-foreground': '0 0% 100%',
      '--primary': '345 86% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 12%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 18%',
      '--muted-foreground': '0 0% 80%',
      '--accent': '330 100% 70%',
      '--accent-foreground': '0 0% 10%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '0 0% 14%',
      '--input': '0 0% 14%',
      '--ring': '345 86% 60%',
    },
  },

  {
    name: 'Light',
    type: 'free',
    variables: {
      '--background': '270 30% 98%',
      '--foreground': '270 10% 10%',
      '--card': '270 30% 98%',
      '--card-foreground': '270 10% 10%',
      '--popover': '270 30% 98%',
      '--popover-foreground': '270 10% 10%',
      '--primary': '270 70% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '270 32.6% 90%',
      '--secondary-foreground': '270 10% 10%',
      '--muted': '270 32.6% 95%',
      '--muted-foreground': '270 20.2% 40%',
      '--accent': '270 32.6% 92%',
      '--accent-foreground': '270 10% 10%',
      '--destructive': '0 62.8% 90%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '270 32.6% 90%',
      '--input': '270 32.6% 90%',
      '--ring': '270 70% 60%',
    },
  },

  {
    name: 'Sakura',
    type: 'free',
    variables: {
      '--background': '340 69% 94%', // #ffe7de (lightest pink for background)
      '--foreground': '336 45% 26%', // #562135 (deep plum for text)
      '--card': '340 72% 91%', // #fcd1d7 (soft pink for cards)
      '--card-foreground': '336 45% 26%',
      '--popover': '340 72% 91%',
      '--popover-foreground': '336 45% 26%',
      '--primary': '328 34% 64%', // #e9b1cd
      '--primary-foreground': '336 45% 26%',
      '--secondary': '329 33% 64%', // #c3829e
      '--secondary-foreground': '336 45% 26%',
      '--muted': '340 69% 94%', // #ffe7de
      '--muted-foreground': '336 45% 26%',
      '--accent': '336 45% 26%', // #562135 (deep plum as accent)
      '--accent-foreground': '340 69% 94%', // #ffe7de (light for accent text)
      '--destructive': '0 70% 40%', // fallback
      '--destructive-foreground': '336 45% 26%',
      '--border': '329 33% 64%', // #c3829e
      '--input': '340 72% 91%', // #fcd1d7
      '--ring': '328 34% 64%', // #e9b1cd
    },
  },
  {
    name: 'Eva Unit-01',
    type: 'free',
    variables: {
      '--background': '267 56% 12%',
      '--foreground': '120 100% 97%',
      '--card': '267 56% 16%',
      '--card-foreground': '120 100% 97%',
      '--popover': '267 56% 16%',
      '--popover-foreground': '120 100% 97%',
      '--primary': '120 100% 50%',
      '--primary-foreground': '267 56% 12%',
      '--secondary': '0 0% 10%',
      '--secondary-foreground': '120 100% 97%',
      '--muted': '267 25% 30%',
      '--muted-foreground': '120 100% 97%',
      '--accent': '300 100% 50%',
      '--accent-foreground': '120 100% 97%',
      '--destructive': '0 100% 50%',
      '--destructive-foreground': '120 100% 97%',
      '--border': '120 100% 50%',
      '--input': '267 25% 30%',
      '--ring': '120 100% 50%',
    },
  },
  {
    name: 'Totoro Forest',
    type: 'free',
    variables: {
      '--background': '120 18% 20%', // Forest green
      '--foreground': '120 18% 10%', // Much darker green for text
      '--card': '120 18% 25%',
      '--card-foreground': '120 18% 10%',
      '--popover': '120 18% 25%',
      '--popover-foreground': '120 18% 10%',
      '--primary': '200 80% 70%',
      '--primary-foreground': '120 18% 10%',
      '--secondary': '30 30% 40%',
      '--secondary-foreground': '120 18% 10%',
      '--muted': '0 0% 60%',
      '--muted-foreground': '120 18% 10%',
      '--accent': '200 80% 70%',
      '--accent-foreground': '120 18% 10%',
      '--destructive': '0 100% 50%',
      '--destructive-foreground': '120 18% 10%',
      '--border': '120 18% 30%',
      '--input': '0 0% 60%',
      '--ring': '200 80% 70%',
    },
  },
  {
    name: 'Straw Hat',
    type: 'free-temp',
    variables: {
      '--background': '48 100% 94%',
      '--foreground': '24 60% 20%',
      '--card': '48 100% 97%',
      '--card-foreground': '24 60% 20%',
      '--popover': '48 100% 97%',
      '--popover-foreground': '24 60% 20%',
      '--primary': '0 85% 60%',
      '--primary-foreground': '48 100% 94%',
      '--secondary': '210 80% 60%',
      '--secondary-foreground': '48 100% 94%',
      '--muted': '48 100% 90%',
      '--muted-foreground': '24 60% 20%',
      '--accent': '0 85% 60%',
      '--accent-foreground': '48 100% 94%',
      '--destructive': '0 100% 50%',
      '--destructive-foreground': '48 100% 94%',
      '--border': '24 60% 40%',
      '--input': '48 100% 90%',
      '--ring': '0 85% 60%',
    },
  },
  {
    name: 'Midnight',
    type: 'free-temp',
    variables: {
      '--background': '240 20% 7%',
      '--foreground': '220 16% 92%',
      '--card': '240 21% 10%',
      '--card-foreground': '220 16% 92%',
      '--popover': '240 21% 10%',
      '--popover-foreground': '220 16% 92%',
      '--primary': '235 100% 62%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '240 10% 18%',
      '--secondary-foreground': '220 16% 92%',
      '--muted': '240 10% 13%',
      '--muted-foreground': '220 16% 60%',
      '--accent': '235 100% 62%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 72% 45%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '240 10% 18%',
      '--input': '240 10% 18%',
      '--ring': '235 100% 62%',
    },
  },
  {
    name: 'Forest',
    type: 'free-temp',
    variables: {
      '--background': '140 21% 10%',
      '--foreground': '120 15% 90%',
      '--card': '140 22% 14%',
      '--card-foreground': '120 15% 90%',
      '--popover': '140 22% 14%',
      '--popover-foreground': '120 15% 90%',
      '--primary': '135 70% 45%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '140 10% 18%',
      '--secondary-foreground': '120 15% 90%',
      '--muted': '140 10% 13%',
      '--muted-foreground': '120 10% 60%',
      '--accent': '135 70% 45%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 72% 45%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '140 10% 18%',
      '--input': '140 10% 18%',
      '--ring': '135 70% 45%',
    },
  },
  {
    name: 'Ocean',
    type: 'free-temp',
    variables: {
      '--background': '200 36% 12%',
      '--foreground': '200 30% 95%',
      '--card': '200 36% 16%',
      '--card-foreground': '200 30% 95%',
      '--popover': '200 36% 16%',
      '--popover-foreground': '200 30% 95%',
      '--primary': '196 100% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '200 18% 24%',
      '--secondary-foreground': '200 30% 95%',
      '--muted': '200 18% 19%',
      '--muted-foreground': '200 10% 60%',
      '--accent': '196 100% 60%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 72% 45%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '200 18% 24%',
      '--input': '200 18% 24%',
      '--ring': '196 100% 60%',
    },
  },
  {
    name: 'Neon',
    type: 'free',
    variables: {
      '--background': '260 70% 8%',
      '--foreground': '60 100% 90%',
      '--card': '260 70% 12%',
      '--card-foreground': '60 100% 90%',
      '--popover': '260 70% 12%',
      '--popover-foreground': '60 100% 90%',
      '--primary': '120 100% 50%',
      '--primary-foreground': '260 70% 8%',
      '--secondary': '300 100% 50%',
      '--secondary-foreground': '60 100% 90%',
      '--muted': '260 70% 10%',
      '--muted-foreground': '60 100% 60%',
      '--accent': '51 100% 50%',
      '--accent-foreground': '260 70% 8%',
      '--destructive': '0 90% 56%',
      '--destructive-foreground': '60 100% 90%',
      '--border': '260 70% 14%',
      '--input': '260 70% 14%',
      '--ring': '120 100% 50%',
    },
  },
  {
    name: 'Violet',
    type: 'free-temp',
    variables: {
      '--background': '258 40% 12%',
      '--foreground': '258 30% 96%',
      '--card': '258 40% 16%',
      '--card-foreground': '258 30% 96%',
      '--popover': '258 40% 16%',
      '--popover-foreground': '258 30% 96%',
      '--primary': '265 90% 70%',
      '--primary-foreground': '258 40% 12%',
      '--secondary': '245 60% 40%',
      '--secondary-foreground': '258 30% 96%',
      '--muted': '258 40% 14%',
      '--muted-foreground': '258 20% 60%',
      '--accent': '285 100% 60%',
      '--accent-foreground': '258 40% 12%',
      '--destructive': '0 90% 56%',
      '--destructive-foreground': '258 30% 96%',
      '--border': '258 40% 20%',
      '--input': '258 40% 20%',
      '--ring': '265 90% 70%',
    },
  },
  {
    name: 'Pumpkin',
    type: 'free',
    variables: {
      '--background': '32 70% 10%',
      '--foreground': '32 100% 95%',
      '--card': '32 70% 14%',
      '--card-foreground': '32 100% 95%',
      '--popover': '32 70% 14%',
      '--popover-foreground': '32 100% 95%',
      '--primary': '24 100% 50%',
      '--primary-foreground': '32 70% 10%',
      '--secondary': '40 100% 60%',
      '--secondary-foreground': '32 100% 95%',
      '--muted': '32 70% 12%',
      '--muted-foreground': '32 100% 60%',
      '--accent': '14 100% 50%',
      '--accent-foreground': '32 70% 10%',
      '--destructive': '0 90% 56%',
      '--destructive-foreground': '32 100% 95%',
      '--border': '32 70% 18%',
      '--input': '32 70% 18%',
      '--ring': '24 100% 50%',
    },
  },
  {
    name: 'Blue',
    type: 'free-temp',
    variables: {
      '--background': '222, 47%, 15%',
      '--foreground': '210, 100%, 98%',
      '--primary': '217, 91%, 60%',
      '--primary-foreground': '0, 0%, 100%',
      '--secondary': '222, 47%, 21%',
      '--secondary-foreground': '210, 100%, 98%',
      '--accent': '217, 91%, 60%',
      '--accent-foreground': '0, 0%, 100%',
      '--muted': '222, 47%, 18%',
      '--muted-foreground': '210, 100%, 98%',
      '--destructive': '0, 84%, 60%',
      '--destructive-foreground': '0, 0%, 100%',
      '--card': '222, 47%, 17%',
      '--card-foreground': '210, 100%, 98%',
      '--border': '222, 47%, 25%',
      '--input': '222, 47%, 25%',
      '--ring': '217, 91%, 60%',
    },
  },
  {
    name: 'Teal',
    type: 'free-temp',
    variables: {
      '--background': '168, 34%, 16%',
      '--foreground': '168, 100%, 97%',
      '--primary': '168, 76%, 42%',
      '--primary-foreground': '168, 100%, 97%',
      '--secondary': '168, 34%, 21%',
      '--secondary-foreground': '168, 100%, 97%',
      '--accent': '168, 76%, 42%',
      '--accent-foreground': '168, 100%, 97%',
      '--muted': '168, 34%, 19%',
      '--muted-foreground': '168, 100%, 97%',
      '--destructive': '0, 84%, 60%',
      '--destructive-foreground': '0, 0%, 100%',
      '--card': '168, 34%, 18%',
      '--card-foreground': '168, 100%, 97%',
      '--border': '168, 34%, 25%',
      '--input': '168, 34%, 25%',
      '--ring': '168, 76%, 42%',
    },
  },
  {
    name: 'Red',
    type: 'free',
    variables: {
      '--background': '0, 62%, 15%',
      '--foreground': '0, 0%, 100%',
      '--primary': '0, 84%, 60%',
      '--primary-foreground': '0, 0%, 100%',
      '--secondary': '0, 62%, 21%',
      '--secondary-foreground': '0, 0%, 100%',
      '--accent': '0, 84%, 60%',
      '--accent-foreground': '0, 0%, 100%',
      '--muted': '0, 62%, 18%',
      '--muted-foreground': '0, 0%, 100%',
      '--destructive': '0, 84%, 60%',
      '--destructive-foreground': '0, 0%, 100%',
      '--card': '0, 62%, 17%',
      '--card-foreground': '0, 0%, 100%',
      '--border': '0, 62%, 25%',
      '--input': '0, 62%, 25%',
      '--ring': '0, 84%, 60%',
    },
  },
  {
    name: 'Gray',
    type: 'free-temp',
    variables: {
      '--background': '0, 0%, 13%',
      '--foreground': '0, 0%, 85%',
      '--primary': '0, 0%, 40%',
      '--primary-foreground': '0, 0%, 100%',
      '--secondary': '0, 0%, 20%',
      '--secondary-foreground': '0, 0%, 85%',
      '--accent': '0, 0%, 40%',
      '--accent-foreground': '0, 0%, 100%',
      '--muted': '0, 0%, 18%',
      '--muted-foreground': '0, 0%, 85%',
      '--destructive': '0, 84%, 60%',
      '--destructive-foreground': '0, 0%, 100%',
      '--card': '0, 0%, 15%',
      '--card-foreground': '0, 0%, 85%',
      '--border': '0, 0%, 25%',
      '--input': '0, 0%, 25%',
      '--ring': '0, 0%, 40%',
    },
  },
  {
    name: 'Sunset',
    type: 'free',
    variables: {
      '--background': '25 90% 15%',
      '--foreground': '35 100% 95%',
      '--card': '25 90% 18%',
      '--card-foreground': '35 100% 95%',
      '--popover': '25 90% 18%',
      '--popover-foreground': '35 100% 95%',
      '--primary': '15 100% 60%',
      '--primary-foreground': '35 100% 95%',
      '--secondary': '340 80% 60%',
      '--secondary-foreground': '35 100% 95%',
      '--muted': '25 90% 20%',
      '--muted-foreground': '35 100% 60%',
      '--accent': '45 100% 50%',
      '--accent-foreground': '25 90% 15%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '35 100% 95%',
      '--border': '25 90% 25%',
      '--input': '25 90% 25%',
      '--ring': '15 100% 60%',
    },
  },
  {
    name: 'Cyberpunk',
    type: 'free',
    variables: {
      '--background': '290 90% 10%',
      '--foreground': '300 100% 95%',
      '--card': '290 90% 13%',
      '--card-foreground': '300 100% 95%',
      '--popover': '290 90% 13%',
      '--popover-foreground': '300 100% 95%',
      '--primary': '320 100% 55%',
      '--primary-foreground': '290 90% 10%',
      '--secondary': '190 100% 50%',
      '--secondary-foreground': '300 100% 95%',
      '--muted': '290 90% 15%',
      '--muted-foreground': '300 100% 60%',
      '--accent': '55 100% 60%',
      '--accent-foreground': '290 90% 10%',
      '--destructive': '0 90% 60%',
      '--destructive-foreground': '300 100% 95%',
      '--border': '290 90% 22%',
      '--input': '290 90% 22%',
      '--ring': '320 100% 55%',
    },
  },
  {
    name: 'Coffee',
    type: 'free-temp',
    variables: {
      '--background': '30 30% 10%',
      '--foreground': '30 20% 90%',
      '--card': '30 30% 13%',
      '--card-foreground': '30 20% 90%',
      '--popover': '30 30% 13%',
      '--popover-foreground': '30 20% 90%',
      '--primary': '25 60% 40%',
      '--primary-foreground': '30 20% 90%',
      '--secondary': '20 40% 25%',
      '--secondary-foreground': '30 20% 90%',
      '--muted': '30 30% 16%',
      '--muted-foreground': '30 20% 60%',
      '--accent': '40 60% 50%',
      '--accent-foreground': '30 30% 10%',
      '--destructive': '0 70% 40%',
      '--destructive-foreground': '30 20% 90%',
      '--border': '30 30% 20%',
      '--input': '30 30% 20%',
      '--ring': '25 60% 40%',
    },
  },
  {
    name: 'Minecraft',
    type: 'free',
    variables: {
      '--background': '110 30% 18%', // deep green (grass)
      '--foreground': '30 30% 90%', // light sand/wood
      '--card': '30 30% 25%', // brown (dirt)
      '--card-foreground': '30 30% 90%',
      '--popover': '110 30% 18%',
      '--popover-foreground': '30 30% 90%',
      '--primary': '110 60% 40%', // vibrant green
      '--primary-foreground': '30 30% 90%',
      '--secondary': '30 30% 40%', // brown/wood
      '--secondary-foreground': '30 30% 90%',
      '--muted': '210 30% 60%', // blue (water/sky)
      '--muted-foreground': '30 30% 90%',
      '--accent': '0 0% 60%', // gray (stone)
      '--accent-foreground': '30 30% 90%',
      '--destructive': '0 70% 40%',
      '--destructive-foreground': '30 30% 90%',
      '--border': '30 30% 40%',
      '--input': '30 30% 40%',
      '--ring': '110 60% 40%',
    },
  },
  {
    name: 'Pikachu',
    type: 'free-temp',
    variables: {
      '--background': '52 100% 92%', // soft yellow
      '--foreground': '40 60% 15%', // dark brown
      '--card': '52 100% 96%', // lighter yellow
      '--card-foreground': '40 60% 15%',
      '--popover': '52 100% 96%',
      '--popover-foreground': '40 60% 15%',
      '--primary': '48 100% 50%', // bright yellow
      '--primary-foreground': '40 60% 15%',
      '--secondary': '0 90% 60%', // red
      '--secondary-foreground': '52 100% 92%',
      '--muted': '52 100% 98%', // very pale yellow
      '--muted-foreground': '40 60% 15%',
      '--accent': '0 0% 0%', // black
      '--accent-foreground': '52 100% 92%',
      '--destructive': '20 100% 50%', // orange
      '--destructive-foreground': '52 100% 92%',
      '--border': '48 100% 70%', // yellow
      '--input': '52 100% 96%', // lighter yellow
      '--ring': '48 100% 50%',
    },
  },
];
